import { useEffect, useState } from 'react';
import { familyOutline, familyPageUrls, familyTextPageUrls, parseOutline } from '../../utils/libraryFamily';
import { freeDesignRef } from '../../utils/freeDesignOutline';
import { outlineHasStoredAnswers } from '../../utils/worksheetAnswerKey';
import useAnswerKeyPages from './useAnswerKeyPages';
import useHydratedPptOutline from './useHydratedPptOutline';
import useHydratedWorksheetOutline from './useHydratedWorksheetOutline';

/**
 * 내 자료·나눔터 상세가 같은 가족 미리보기 상태를 쓴다.
 * item 은 라이브러리 가족 모양(나눔터는 galleryItemAsFamily 이후).
 */
export default function useLibraryFamilyView(item, viewMode, pageKind) {
  const textOutline = familyOutline(item, 'text') || item?.outline;
  const designRef = item?.content_type === 'ppt'
    ? (freeDesignRef(textOutline) || freeDesignRef(parseOutline(item?.outline)))
    : null;
  const designPageUrls = (designRef?.preview_urls || []).filter((url) => typeof url === 'string' && url);
  const displayOutline = useHydratedPptOutline(
    item?.content_type === 'ppt' && designPageUrls.length === 0 ? textOutline : null,
    item?.id,
  );
  const textPageUrls = item?.content_type === 'worksheet' ? familyTextPageUrls(item) : [];
  const { outline: worksheetCanvasOutline, slides: worksheetPages, pending: worksheetPending } = useHydratedWorksheetOutline(
    item?.content_type === 'worksheet' && textPageUrls.length === 0
      ? textOutline
      : null,
    item?.id,
  );
  const hasStoredAnswers = item?.content_type === 'worksheet' && outlineHasStoredAnswers(textOutline);
  const { slides: answerKeyPages, themeId: answerKeyThemeId } = useAnswerKeyPages(
    item?.content_type === 'worksheet' ? textOutline : null,
    item?.id,
    item?.content_type === 'worksheet' && hasStoredAnswers && pageKind === 'answer',
  );
  const [wsVisibleCount, setWsVisibleCount] = useState(1);

  useEffect(() => {
    if (item?.content_type !== 'worksheet' || viewMode !== 'text' || worksheetPages.length === 0) {
      return undefined;
    }
    setWsVisibleCount(1);
    if (worksheetPages.length <= 1) return undefined;
    const timer = window.setTimeout(() => setWsVisibleCount(worksheetPages.length), 0);
    return () => window.clearTimeout(timer);
  }, [item?.content_type, item?.id, viewMode, worksheetPages.length]);

  const isFamily = item?.content_type === 'ppt' || item?.content_type === 'worksheet';
  const imageUrls = familyPageUrls(item);
  const showAnswerKey = item?.content_type === 'worksheet' && pageKind === 'answer';
  const imageModeUrls = isFamily && viewMode === 'image' && !showAnswerKey
    ? (imageUrls.length ? imageUrls : (item?.preview_urls || [])).filter((url) => typeof url === 'string' && url)
    : [];
  // 이미지형 PPT 도 텍스트형처럼 모든 장을 한눈에 보는 그리드로 보여 준다.
  const pptImagePageUrls = item?.content_type === 'ppt' ? imageModeUrls : [];
  const allPreviews = isFamily
    ? (pptImagePageUrls.length ? [] : imageModeUrls)
    : (item?.preview_urls?.length > 0
      ? item.preview_urls
      : (item?.thumbnail_url ? [item.thumbnail_url] : []));
  const outline = item?.content_type === 'ppt' ? displayOutline : textOutline;
  const slides = outline?.slides || [];
  const questions = outline?.questions || [];
  const sections = outline?.sections || [];
  const showTextViewer = !isFamily || viewMode === 'text';
  const showWsPageImages = showTextViewer && !showAnswerKey && item?.content_type === 'worksheet' && textPageUrls.length > 0;
  const showWsCanvasPages = showTextViewer && !showAnswerKey && item?.content_type === 'worksheet' && !showWsPageImages && worksheetPages.length > 0;
  const visibleWorksheetPages = showWsCanvasPages
    ? worksheetPages.slice(0, Math.max(1, Math.min(wsVisibleCount, worksheetPages.length)))
    : [];
  const worksheetThemeId = worksheetCanvasOutline?.canvas_theme || outline?.canvas_theme;
  // 미리 렌더된 쪽 이미지(page_urls)는 A4 세로 이미지 슬라이드로 풀스크린에 넘긴다(모바일에서 탭→확대·스와이프).
  const textPageSlides = textPageUrls.map((url, i) => ({
    slide_id: `text-page-${i + 1}`,
    page_image_url: url,
    canvas: { width: 794, height: 1123 },
  }));
  const designSlides = designPageUrls.map((url, i) => ({
    slide_id: `design-${i + 1}`,
    page_image_url: url,
    canvas: { width: 1280, height: 720 },
  }));
  const pptImageSlides = pptImagePageUrls.map((url, i) => ({
    slide_id: `image-${i + 1}`,
    page_image_url: url,
    canvas: { width: 1280, height: 720 },
  }));
  const pptSlides = pptImageSlides.length ? pptImageSlides : (designSlides.length ? designSlides : slides);
  const fullscreenSlides = showAnswerKey
    ? answerKeyPages
    : (item?.content_type === 'worksheet' ? (textPageSlides.length ? textPageSlides : worksheetPages) : pptSlides);
  const fullscreenThemeId = showAnswerKey
    ? (answerKeyThemeId || worksheetThemeId)
    : (item?.content_type === 'worksheet' ? worksheetThemeId : outline?.canvas_theme);

  return {
    allPreviews,
    answerKeyPages,
    answerKeyThemeId,
    designPageUrls,
    fullscreenSlides,
    fullscreenThemeId,
    hasStoredAnswers,
    imageUrls,
    isFamily,
    outline,
    pptImagePageUrls,
    questions,
    sections,
    showAnswerKey,
    showTextViewer,
    showWsCanvasPages,
    showWsPageImages,
    slides,
    textOutline,
    textPageUrls,
    visibleWorksheetPages,
    worksheetPages,
    worksheetPending,
    worksheetThemeId,
  };
}
